import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { useDragResize } from '../../../shared/hooks/useDragResize';
import { useComments, useAddComment, useStages } from '../hooks/useClientData';
import { useOpFlowCanvas } from '../hooks/useOpFlowCanvas';
import { PRODUCT_COLORS } from '../lib/constants';
import WhatsAppPanel from './WhatsAppPanel';
import { SubPanelTratos } from './SubPanelTratos';
import OpCommentsTab from './OpCommentsTab';
import FlowToolbar from './FlowToolbar';
import FlowNode from './FlowNode';

// Fase 5.15 -- presentacional: `onNavigate` (antes `useNavigate`), `urlTab` / `urlNodeId` /
// `spacePrefix` (antes `useParams`) y `toast` llegan por prop del container (`ClientsView`, que
// a su vez los recibe de `ClientsContainer`).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: sincroniza la pestaña activa del panel con el segmento de tab de la URL.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
export default function OpDetailPanel({ op, currentUserId, currentUserNick, currentUserImg, onClose, onSaved, onTabChange, overlayWidth, setOverlayWidth, onNavigate, urlTab, urlNodeId, spacePrefix = '', toast }) {
  const { t } = useTranslation('client');
  const navigate = onNavigate;

  const [leftWidth, setLeftWidth] = React.useState(540);
  const [localRightWidth, setLocalRightWidth] = React.useState(600);
  const rightWidth = overlayWidth !== undefined ? overlayWidth : localRightWidth;
  const setRightWidth = setOverlayWidth !== undefined ? setOverlayWidth : setLocalRightWidth;

  // Hook compartido en vez de mousedown→window.addEventListener('mousemove'/
  // 'mouseup') sin limpieza si el mouse se suelta fuera del viewport. Ninguno de los dos
  // persiste a red — solo estado local (`rightWidth`/`leftWidth`), así que no hace falta `onEnd`.
  const startResizeRightDrag = useDragResize({
    onMove: ({ moveEvent }) => {
      const newWidth = Math.max(320, Math.min(window.innerWidth - 100, window.innerWidth - moveEvent.clientX));
      setRightWidth(newWidth);
      return newWidth;
    },
  });
  const startResizeRight = (e) => startResizeRightDrag(e, rightWidth);

  const startResizeLeftDrag = useDragResize({
    onMove: ({ moveEvent }) => {
      const rightEdge = window.innerWidth - rightWidth;
      const newWidth = Math.max(320, Math.min(window.innerWidth - rightWidth - 50, rightEdge - moveEvent.clientX));
      setLeftWidth(newWidth);
      return newWidth;
    },
  });
  const startResizeLeft = (e) => startResizeLeftDrag(e, leftWidth);

  const [activeTab, setActiveTab] = React.useState(() => {
    if (urlTab && urlTab !== 'proceso') return urlTab;
    if (window.location.pathname.includes('/flow')) return 'flow';
    if (window.location.pathname.includes('/config')) return 'config';
    return 'flow';
  });

  const [showWhatsPanel, setShowWhatsPanel] = React.useState(false);

  React.useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab === 'proceso' ? 'flow' : urlTab);
    }
  }, [urlTab]);

  const changeTab = (tabId) => {
    setActiveTab(tabId);
    if (op.short_id) {
       navigate(`${spacePrefix}/database/clientes/${op.short_id}/${tabId}`, { replace: true });
    }
    onTabChange?.(tabId);
  };
  const { data: comments = [] } = useComments(op.id);
  const { data: stages = [] }   = useStages(op.id);
  const addCommentMutation = useAddComment(op.id);
  const [activeStageId] = React.useState(op.active_stage_id || null);
  const [newComment, setNewComment] = React.useState('');
  const [sending, setSending] = React.useState(false);

  // 04-enforce.md 4.1 -- el motor del canvas del flow (pan/zoom/drag/resize/conexión de nodos +
  // carga/guardado del flow + nodo seleccionado, ~300 líneas operativas) vive ahora en
  // `useOpFlowCanvas`. `useClientDocumentsByUser`/`useClientFlow` (dentro del hook) comparten
  // cache con la carga que `SubPanelTratos.jsx` hace del mismo `op.id`.
  const {
    flowContainerRef, flowSvgRef,
    flowPos, flowScale,
    flowNodes, flowEdges,
    flowSearch, setFlowSearch,
    flowConnecting,
    selectedTriggerNode, closeSelectedTriggerNode,
    flowDropdown, setFlowDropdown,
    flowDdSearch, setFlowDdSearch,
    allUsers,
    opDocs, setOpDocs,
    flowDraggingNode,
    handleDeleteFlowNode, handleDeleteEtapaContainer, handleEditFlowNode,
    addFlowNode,
    startNodeDrag, startNodeResize, startConnect, endConnect,
    portCoord,
    viewportHandlers,
  } = useOpFlowCanvas({ op, navigate, spacePrefix, urlNodeId });

  const accordionKey = (stage) => stage.id === null ? 'cotizacion' : stage.id;

  const sendComment = async () => {
    if (!newComment.trim()) return;
    setSending(true);
    try {
      await addCommentMutation.mutateAsync({ content: newComment, author_id: currentUserId, stage_id: activeStageId });
      setNewComment('');
    } finally {
      setSending(false);
    }
  };

  // Agrupación de comentarios por etapa -- memoizada, no se recalcula con estado no
  // relacionado.
  const allStages = useMemo(() => [
    { id: null, title: 'COTIZACION' },
    ...stages.map(e => ({ id: e.id, title: e.title.toUpperCase() })),
  ], [stages]);
  const commentsByStage = useMemo(() => {
    const byStage = {};
    allStages.forEach(e => { byStage[accordionKey(e)] = []; });
    comments.forEach(c => {
      const key = c.stage_id || 'cotizacion';
      if (byStage[key] !== undefined) byStage[key].push(c);
      else byStage['cotizacion'].push(c);
    });
    return byStage;
  }, [allStages, comments]);

  const TABS = [
    { id: 'flow',     label: t('detailPanel.tabs.flow') },
    { id: 'config',   label: t('detailPanel.tabs.config') },
    { id: 'comments', label: t('detailPanel.tabs.comments') },
  ];

  return (
    <>
      {selectedTriggerNode && (
        <SubPanelTratos key={selectedTriggerNode.id} node={selectedTriggerNode} onClose={closeSelectedTriggerNode} op={op} currentUserId={currentUserId} onSavedDoc={d => setOpDocs(p => ({...p, [d.document_type]: d}))} flowNodes={flowNodes} flowEdges={flowEdges} width={leftWidth} rightOffset={rightWidth} onResizeStart={startResizeLeft} toast={toast} />
      )}
      {showWhatsPanel && (
        <div 
          className="whats-panel" 
          style={{ 
            position: 'fixed',
            right: selectedTriggerNode ? (rightWidth + leftWidth) : rightWidth,
            top: 0,
            bottom: 0,
            width: '350px',
            background: '#111113',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            zIndex: 999998,
            boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.5)',
            animation: 'slideFromRight .2s ease-out',
            overflow: 'hidden'
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <WhatsAppPanel onClose={() => setShowWhatsPanel(false)} toast={toast} />
        </div>
      )}
      <div className="op-detail-panel" style={{ width: rightWidth, position: 'relative' }}>
        {/* Resizer Zone on the left border */}
        <div 
          className="op-resizer-zone"
          style={{
            position: 'absolute',
            left: '-6px',
            top: 0,
            bottom: 0,
            width: '12px',
            cursor: 'ew-resize',
            zIndex: 1000000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all'
          }}
        >
          {/* Resizer Handle */}
          <div 
            className="op-resizer-handle"
            onPointerDown={startResizeRight}
            style={{
              position: 'absolute',
              bottom: '125px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '8px',
              height: '48px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(255, 255, 255, 0)',
              transition: 'background 0.2s, opacity 0.2s',
              cursor: 'ew-resize',
              pointerEvents: 'all'
            }}
          />
        </div>
        {/* Header */}
      <div className="op-panel-header">
        {op.short_id && (
          <button className="op-copy-btn" title={t('detailPanel.copyLinkTitle')} aria-label={t('detailPanel.copyLinkTitle')}
            onClick={() => navigator.clipboard.writeText('https://colonny.xyz/database/clientes/' + op.short_id)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
          </button>
        )}
        {op.product && (
          <span className="op-header-product-tag" style={{
            color: PRODUCT_COLORS[op.product] || '#aaa',
            borderColor: PRODUCT_COLORS[op.product] || '#aaa',
            backgroundColor: (PRODUCT_COLORS[op.product] || '#aaa') + '15'
          }}>{op.product}</span>
        )}
        <div className="op-panel-title">{op.transaction || t('detailPanel.operationFallback')}</div>
        <button className="op-panel-close" aria-label={t('detailPanel.closeAriaLabel')} onClick={onClose}>✕</button>
      </div>

      {/* Tab bar */}
      {/* Tab bar */}
      <div className="op-panel-tabs" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {TABS.map(t => (
            <button key={t.id}
              className={`op-panel-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => changeTab(t.id)}
            >{t.label}</button>
          ))}
          <button
            onClick={() => setShowWhatsPanel(!showWhatsPanel)}
            title={t('detailPanel.whatsappPanelTitle')}
            aria-label={t('detailPanel.whatsappPanelTitle')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: showWhatsPanel ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.35)',
              padding: 'var(--space-1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              marginLeft: 'var(--space-2)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)'}
            onMouseLeave={e => e.currentTarget.style.color = showWhatsPanel ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.35)'}
          >
            <MessageSquare size={16} />
          </button>
        </div>
      </div>

      {/* Tab: Flow */}
      {activeTab === 'flow' && (
        <div className="op-panel-body op-flow-body">

          {/* ── Toolbar overlay ──────────────────────────────────────────── */}
          <FlowToolbar
            allUsers={allUsers}
            flowDropdown={flowDropdown}
            setFlowDropdown={setFlowDropdown}
            flowDdSearch={flowDdSearch}
            setFlowDdSearch={setFlowDdSearch}
            flowSearch={flowSearch}
            setFlowSearch={setFlowSearch}
            onAddNode={addFlowNode}
          />

          {/* ── Viewport container ───────────────────────────────────────── */}
          <div
            className="op-flow-viewport"
            ref={flowContainerRef}
            {...viewportHandlers}
          >
            {/* Infinite canvas */}
            <div
              className="op-flow-canvas"
              style={{
                transform: `translate(${flowPos.x}px, ${flowPos.y}px) scale(${flowScale})`,
                transformOrigin: '0 0',
              }}
            >
              {/* Dot grid */}
              <svg
                className="op-flow-dots"
                xmlns="http://www.w3.org/2000/svg"
                width="800" height="1000"
              >
                <defs>
                  <pattern id="op-dot-pattern" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="rgba(180,180,180,0.28)" />
                  </pattern>
                </defs>
                <rect width="800" height="1000" fill="url(#op-dot-pattern)" />
              </svg>

              {/* ── Edges SVG layer ──────────────────────────────────────── */}
              <svg
                ref={flowSvgRef}
                style={{ position:'absolute', top:0, left:0, width:800, height:1000, overflow:'visible', pointerEvents:'none', zIndex:100 }}
              >
                <defs>
                  <marker id="op-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L8,3 z" fill="rgba(255,255,255,0.4)"/>
                  </marker>
                </defs>
                {flowEdges.map(edge => {
                  const s = portCoord(edge.from, 'out', edge.portIndex ?? 0);
                  const t = portCoord(edge.to,   'in', edge.toPortIndex ?? 0);
                  const cx1 = s.x + 60, cy1 = s.y, cx2 = t.x - 60, cy2 = t.y;
                  return (
                    <path
                      key={edge.id}
                      d={`M${s.x},${s.y} C${cx1},${cy1} ${cx2},${cy2} ${t.x},${t.y}`}
                      stroke="rgba(255,255,255,0.35)"
                      strokeWidth="1.5"
                      fill="none"
                      markerEnd="url(#op-arrow)"
                    />
                  );
                })}
                {/* Live connecting line */}
                {flowConnecting && (() => {
                  const s = portCoord(flowConnecting.fromId, 'out', flowConnecting.portIndex ?? 0);
                  // convert container coords to canvas coords
                  const tx = (flowConnecting.curX - flowPos.x) / flowScale;
                  const ty = (flowConnecting.curY - flowPos.y) / flowScale;
                  return (
                    <path
                      d={`M${s.x},${s.y} C${s.x+60},${s.y} ${tx-60},${ty} ${tx},${ty}`}
                      stroke="rgba(255,255,255,0.55)"
                      strokeWidth="1.5"
                      strokeDasharray="5 3"
                      fill="none"
                    />
                  );
                })()}
              </svg>

              {/* ── Nodes ───────────────────────────────────────────────── */}
              {flowNodes.map(node => (
                <FlowNode
                  key={node.id}
                  node={node}
                  flowNodes={flowNodes}
                  flowEdges={flowEdges}
                  flowSearch={flowSearch}
                  dragging={flowDraggingNode?.id === node.id}
                  opDocs={opDocs}
                  onStartDrag={startNodeDrag}
                  onStartResize={startNodeResize}
                  onStartConnect={startConnect}
                  onEndConnect={endConnect}
                  onDelete={handleDeleteFlowNode}
                  onDeleteEtapaContainer={handleDeleteEtapaContainer}
                  onEdit={handleEditFlowNode}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Configuración */}
      {activeTab === 'config' && (
        <div className="op-panel-body op-tab-empty-body">
          <div className="op-tab-placeholder">
            <div className="op-tab-placeholder-icon">{'⚙️'}</div>
            <div>{t('detailPanel.configPlaceholder')}</div>
            <div style={{fontSize:11,opacity:0.4}}>{t('detailPanel.comingSoon')}</div>
          </div>
        </div>
      )}

      {/* Tab: Comentarios -- `comments`/`newComment`/`sendComment`/`sending`/`commentsByStage`/
          `allStages`/`accordionKey` ya estaban conectados (datos + mutación + agrupación por
          etapa) desde antes, sin ningún JSX que los renderizara -- encontrado en pruebas E2E
          (2026-07-15). Extraído a `OpCommentsTab.jsx` en el paso b (02.3-client.md). */}
      {activeTab === 'comments' && (
        <OpCommentsTab
          comments={comments}
          allStages={allStages}
          commentsByStage={commentsByStage}
          accordionKey={accordionKey}
          allUsers={allUsers}
          newComment={newComment}
          setNewComment={setNewComment}
          sendComment={sendComment}
          sending={sending}
        />
      )}
    </div>
    </>
  );
}
